import { default as React, useEffect, useState } from "react";
import { Route, BrowserRouter as Router, Routes, Navigate } from "react-router-dom";
import {
  ClientWebSocketService,
  WebSocketContext
} from "../services/ClientWebSocketService";
import AdminPage from "./pages/Admin/AdminPage";
import QuestionnairePage from "./QuestionnaireUnified/QuestionnairePage";
import Home from "./pages/Home";
import OperatorPage from "./pages/Operator/OperatorPage";
import SimpleRegister from "./pages/PageUtils/General/SimpleRegister";
import HTMLViewer from "./pages/Questionnaire/RefrencesFetcher/ResourcesRendering/HTMLViewer";
import PDFViewer from "./pages/Questionnaire/RefrencesFetcher/ResourcesRendering/ProtectedPdf";
import GlobalSnackbar from './GlobalSnackbar';

import api from "../services/api";

// Helper function to check if user is authenticated and validate with server
const validateAuth = async (role) => {
  const cookie = localStorage.getItem("cookie");
  const username = localStorage.getItem("username");

  if (!cookie || !username) {
    return false;
  }

  try {
    const endpoint = role === 'admin' ? "/admin/verify" : "/operator/verify";
    await api.get(endpoint);
    return true;
  } catch (error) {
    if (error.response?.status === 401) {
      return false;
    }
    console.error("Auth validation error:", error);
    return false;
  }
};

// Protected route wrapper component
const ProtectedRoute = ({ children, redirectPath, role }) => {
  const [isAuthed, setIsAuthed] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const checkAuth = async () => {
      const valid = await validateAuth(role);
      setIsAuthed(valid);
      if (!valid) {
        localStorage.removeItem("cookie");
        localStorage.removeItem("username");
      }
      setLoading(false);
    };

    checkAuth();

    // Periodically validate auth (every 30 seconds) to catch old tabs with invalid cookies
    const interval = setInterval(checkAuth, 30000);
    return () => clearInterval(interval);
  }, [role]);

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!isAuthed) {
    return <Navigate to={redirectPath} replace />;
  }
  return children;
};

const App = () => {
  const [webSocket, setWebSocket] = useState(null);

  useEffect(() => {
    // Get the WebSocket connection from localStorage
    const storedWebSocket = JSON.parse(localStorage.getItem("webSocket"));

    if (storedWebSocket) {
      // Set the WebSocket connection in state
      setWebSocket(storedWebSocket);
    }
  }, []);

  useEffect(() => {
    // Get the WebSocket connection from localStorage
    const webSocketPort = localStorage.getItem("webSocketPort");

    // Create a new WebSocket connection if the webSocket state is null
    if (webSocket === null && webSocketPort) {
      const username = localStorage.getItem("username");
      const cookie = localStorage.getItem("cookie");
      if (!webSocketPort || !username || !cookie) {
        return;
      }

      const websocket = new ClientWebSocketService(
        username,
        cookie,
        webSocketPort
      );

      setWebSocket(websocket);
    }

    // Periodically check if the WebSocket is still open and reconnect if needed
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible' && webSocket && !webSocket.isConnected()) {
        console.log("WebSocket disconnected and page is visible, attempting to reconnect...");
        setWebSocket(null); // This will trigger the creation effect above
      }
    }, 5000); // Check every 5 seconds

    // Clean up the WebSocket connection when the component unmounts
    return () => {
      clearInterval(interval);
      if (webSocket !== null) {
        webSocket.disconnect();
      }
      // localStorage.removeItem("webSocket");
    };
  }, [webSocket]);

  const setWebSocketConnection = ws => {
    setWebSocket(ws);
  };

  return (
    <WebSocketContext.Provider value={webSocket}>
      <GlobalSnackbar />
      <Router>
        <Routes>
          <Route
            path="/"
            element={
              <Home setWebSocketConnectionFunction={setWebSocketConnection} />
            }
          />
          <Route
            path="/admin"
            element={<SimpleRegister registerType="admin" />}
          />
          <Route
            path="/admin/manager"
            element={
              <ProtectedRoute redirectPath="/admin" role="admin">
                <AdminPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/operator"
            element={<SimpleRegister registerType="operator" />}
          />
          <Route
            path="/operator/manager"
            element={
              <ProtectedRoute redirectPath="/operator" role="operator">
                <OperatorPage />
              </ProtectedRoute>
            }
          />

          <Route path="/questionnaire" element={<QuestionnairePage />} />
          <Route path="/questionnaire/pdf-file" element={<PDFViewer />} />
          <Route path="/questionnaire/html-file" element={<HTMLViewer />} />
        </Routes>
      </Router>
    </WebSocketContext.Provider>
  );
};

export default App;
